// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="ColorSchemePicker" set="avila-roffe-shadcn-studio" verified=tbd

import { Monitor, Moon, Sun } from 'lucide-react';
import type * as React from 'react';
import { cn } from '../../lib/utils';
import { ToggleGroup, ToggleGroupItem } from './toggle-group';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ColorScheme = 'light' | 'system' | 'dark';

export interface ColorSchemePickerProps {
  value?: ColorScheme;
  onValueChange?: (value: ColorScheme) => void;
  className?: string;
}

// ─── Options ──────────────────────────────────────────────────────────────────

const OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'dark', label: 'Dark', icon: Moon },
] as const;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Three-way colour-scheme picker: Light / System / Dark.
 * Renders as a full-width segmented toggle group with icon + label per option.
 * The active option gets a white background card and subtle shadow.
 *
 * @example
 * <ColorSchemePicker value={scheme} onValueChange={setScheme} />
 *
 * @example
 * // Inside RHF with Controller
 * <Controller
 *   control={form.control}
 *   name="colorScheme"
 *   render={({ field }) => (
 *     <ColorSchemePicker value={field.value} onValueChange={field.onChange} />
 *   )}
 * />
 */
export function ColorSchemePicker({
  value = 'system',
  onValueChange,
  className,
}: ColorSchemePickerProps) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => {
        if (v) onValueChange?.(v as ColorScheme);
      }}
      style={
        {
          '--csp-inner-radius': 'calc(max(0px, var(--field-radius, var(--radius)) - 2px))',
        } as React.CSSProperties
      }
      className={cn(
        'inline-flex w-full gap-0 rounded-[var(--field-radius,var(--radius))] border border-border bg-muted/30 p-1',
        className,
      )}
    >
      {OPTIONS.map(({ value: val, label, icon: Icon }) => (
        <ToggleGroupItem
          key={val}
          value={val}
          aria-label={label}
          className="flex h-9 flex-1 cursor-pointer items-center justify-center gap-2 rounded-[var(--csp-inner-radius)] text-sm font-medium text-muted-foreground transition-all hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
        >
          <Icon className="size-4" aria-hidden="true" />
          <span>{label}</span>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

ColorSchemePicker.displayName = 'ColorSchemePicker';
